import React, { useEffect, useState } from 'react';
import { api, mediaUrl } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import { Plus, Trash2, Eye } from 'lucide-react';
import { FileField, TextField, Toggle, Select, Drawer } from '@/components/admin/shared';
import { MarketingPopup, MarketingBanner, MarketingHeroCard } from '@/components/Marketing';

const DEFAULT_POSTER = {
  title: '', subtitle: '', body: '', image_url: '', cta_label: '', cta_url: '',
  enabled: false, placement: 'popup', frequency: 'once_per_session',
  aspect_ratio: 'square_1_1',
  starts_at: '', ends_at: '', order: 0,
};

export default function MarketingManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT_POSTER);
  const [previewing, setPreviewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/marketing'); setItems(data); } catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(DEFAULT_POSTER); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...DEFAULT_POSTER, ...it }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/marketing/${editing.id}`, form);
      else await api.post('/admin/marketing', form);
      setOpen(false); await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this poster?')) return;
    try { await api.delete(`/admin/marketing/${id}`); await load(); } catch (e) { alert(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="admin-marketing-manager">
      <div className="pg-admin-bar flex items-center justify-between mb-4">
        <div>
          <p className="pg-label">MARKETING POSTERS / POPUPS</p>
          <h3 className="font-serif text-xl mt-1 text-[var(--pg-ink)]">Drop a signal, time it well</h3>
          <p className="text-xs text-[var(--pg-ink-muted)] mt-1 max-w-2xl">Create scheduled popups or banners. Toggle them live; expire automatically with date windows.</p>
        </div>
        <button data-testid={ADMIN.createButton} onClick={openCreate} className="pg-btn-primary rounded-[var(--pg-radius-md)] px-4 py-2 text-xs font-mono tracking-widest inline-flex items-center gap-2">
          <Plus size={14} /> NEW POSTER
        </button>
      </div>

      <div className="pg-card overflow-hidden">
        {loading ? <div className="p-10 text-center text-[var(--pg-ink-muted)]">Loading…</div>
        : items.length === 0 ? <div className="p-10 text-center text-[var(--pg-ink-muted)]">No posters yet.</div>
        : (
          <ul className="divide-y divide-[var(--pg-border-2)]">
            {items.map((p) => (
              <li key={p.id} className="p-4 flex items-center gap-4">
                <div className="h-14 w-20 rounded-md overflow-hidden bg-[var(--pg-navy-800)] border border-[var(--pg-border-2)] shrink-0">
                  {p.image_url ? <img src={mediaUrl(p.image_url)} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center text-[var(--pg-ink-faint)] text-[10px] font-mono">NO IMG</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif text-base text-[var(--pg-ink)] truncate">{p.title}</p>
                    <span className="text-[10px] font-mono uppercase" style={{ color: p.enabled ? 'var(--pg-emerald)' : 'var(--pg-ink-faint)' }}>{p.enabled ? 'LIVE' : 'DRAFT'}</span>
                  </div>
                  <p className="text-xs text-[var(--pg-ink-muted)] truncate">{p.placement} · {p.frequency}{p.starts_at ? ` · from ${p.starts_at.slice(0,10)}` : ''}{p.ends_at ? ` to ${p.ends_at.slice(0,10)}` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPreviewing(p)} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid="marketing-preview-button">
                    <Eye size={12} /> Preview
                  </button>
                  <button onClick={() => openEdit(p)} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs">Edit</button>
                  <button onClick={() => del(p.id)} data-testid={ADMIN.deleteButton} className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs text-[var(--pg-neon-pink)]"><Trash2 size={14} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Drawer open={open} onClose={() => setOpen(false)} title={editing ? 'Edit poster' : 'New poster'} onSave={save} saving={saving}>
        <TextField label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
        <TextField label="Subtitle" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} />
        <TextField label="Body" value={form.body} onChange={(v) => setForm({ ...form, body: v })} textarea rows={4} />
        <FileField label="Image" kind="image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} accept="image/*" />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="CTA label" value={form.cta_label} onChange={(v) => setForm({ ...form, cta_label: v })} placeholder="Read more" />
          <TextField label="CTA URL" value={form.cta_url} onChange={(v) => setForm({ ...form, cta_url: v })} placeholder="/only-on-planet-g" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Placement" value={form.placement} onChange={(v) => setForm({ ...form, placement: v })} options={[
            { value: 'popup', label: 'Popup' },
            { value: 'banner', label: 'Banner' },
            { value: 'home_hero', label: 'Home hero' },
          ]} />
          <Select label="Frequency" value={form.frequency} onChange={(v) => setForm({ ...form, frequency: v })} options={[
            { value: 'always', label: 'Always' },
            { value: 'once_per_session', label: 'Once per session' },
            { value: 'once_per_day', label: 'Once per day' },
          ]} />
        </div>
        <Select label="Aspect ratio (how the image is displayed)" value={form.aspect_ratio || 'square_1_1'} onChange={(v) => setForm({ ...form, aspect_ratio: v })} options={[
          { value: 'poster_2_3', label: 'Poster (2:3) — tall portrait' },
          { value: 'square_1_1', label: 'Album cover (1:1) — square' },
          { value: 'full_auto', label: 'Full image (auto / contain — no crop)' },
        ]} />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Starts at (ISO, optional)" value={form.starts_at} onChange={(v) => setForm({ ...form, starts_at: v })} placeholder="2026-01-01T00:00:00Z" />
          <TextField label="Ends at (ISO, optional)" value={form.ends_at} onChange={(v) => setForm({ ...form, ends_at: v })} placeholder="2026-12-31T23:59:59Z" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Order" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: Number(v) || 0 })} />
          <Toggle label="Enabled (live)" value={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} />
        </div>
      </Drawer>

      {/* Preview rendering */}
      {previewing && previewing.placement === 'popup' && (
        <MarketingPopup
          key={`preview-popup-${previewing.id}`}
          poster={previewing}
          immediate
          onDismiss={() => setPreviewing(null)}
        />
      )}
      {previewing && previewing.placement === 'banner' && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 pt-20" data-testid="marketing-preview-overlay">
          <div className="absolute inset-0 bg-[rgba(2,4,10,0.72)] backdrop-blur-sm" onClick={() => setPreviewing(null)} />
          <div className="relative w-full max-w-4xl">
            <MarketingBanner poster={previewing} onDismiss={() => setPreviewing(null)} />
            <p className="absolute -bottom-8 left-0 right-0 text-center text-[10px] font-mono tracking-[0.32em] text-[var(--pg-ink-faint)] uppercase">Banner preview · click outside to close</p>
          </div>
        </div>
      )}
      {previewing && previewing.placement === 'home_hero' && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 overflow-y-auto" data-testid="marketing-preview-overlay">
          <div className="absolute inset-0 bg-[rgba(2,4,10,0.78)] backdrop-blur-md" onClick={() => setPreviewing(null)} />
          <div className="relative w-full max-w-4xl">
            <MarketingHeroCard poster={previewing} />
            <div className="text-center mt-4">
              <button onClick={() => setPreviewing(null)} className="pg-btn-ghost text-[10px] font-mono tracking-[0.32em] uppercase">Close preview</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
